import React, { FC, useEffect } from 'react';
import { InjectedFormProps } from 'redux-form';

import { useTranslations } from '@lang';
import { useRouter } from 'shared-routing';
import Portal from '../../../../components/Portal';
import { Button } from '../../../../components/Button';
import { StickyBarContent } from '../../../../components/StickyBar';
import { Routes } from '../../../../constants';
import { ExtrasProps } from './ExtrasStep.types';
import { Extras } from './Extras';
import { priceWithExtras, mapExtrasToBasket, combineRoomExtras } from './utils';
import { ExtrasWrapper } from './ExtrasWrapper.styles';
import { RoomRateFormStepNames } from '../FormSteps';

const routesMap = {
  room: Routes.ROOM_BOOKING,
  rate: Routes.RATE_BOOKING,
};

export const ExtrasStep: FC<ExtrasProps & InjectedFormProps<{}, ExtrasProps>> = ({
  stickyBarId,
  handleSubmit,
  valid,
  submitting,
  addToBasket,
  goToNextStep,
  selectedExtras = [],
  multiRoomSelectedExtras,
  areGuestsLoaded = false,
  selectedEntity,
  metaEntity,
  entity,
  availableExtras,
  journeyParams,
  guests,
  isMultiRoom,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  useEffect(() => {
    if (areGuestsLoaded) {
      return;
    }

    router.push({
      pathname: routesMap[metaEntity.type],
      query: {
        id: metaEntity.id,
        bookingStep: RoomRateFormStepNames.GUESTS,
        [entity.type]: entity.id,
      },
    });
  }, [areGuestsLoaded]);

  const onSubmit = ({ guests, extras, note }) => {
    if (isMultiRoom) {
      const entExtras = journeyParams.rooms?.map((room, i) => {
        const extrasDetails = extras?.[`room-${i}`];
        return mapExtrasToBasket(availableExtras, extrasDetails);
      });
      addToBasket({ guestDetails: guests, extras: entExtras, note }).then(goToNextStep);
    } else {
      const entExtras = mapExtrasToBasket(availableExtras, extras);
      addToBasket({ guestDetails: guests, extras: entExtras, note }).then(goToNextStep);
    }
  };

  let multiRoomExtras: { isSelected: boolean; quantity: number }[] = [];

  if (isMultiRoom) {
    multiRoomExtras = combineRoomExtras(multiRoomSelectedExtras);
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <ExtrasWrapper>
        <Extras
          extras={availableExtras}
          selectedExtras={selectedExtras}
          journeyParams={journeyParams}
          guests={guests}
          isMultiRoom={isMultiRoom}
        />
      </ExtrasWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          displayedPrice={
            isMultiRoom
              ? priceWithExtras(selectedEntity, availableExtras, multiRoomExtras)
              : priceWithExtras(selectedEntity, availableExtras, selectedExtras)
          }
          button={
            <Button
              isPrimary
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!valid || submitting}
            >
              {t('booking.continue')}
            </Button>
          }
        />
      </Portal>
    </form>
  );
};
